import { NgModule, ErrorHandler, APP_INITIALIZER } from "@angular/core";
import { BrowserModule } from "@angular/platform-browser";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { FormsModule } from "@angular/forms";
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi, withXhr } from "@angular/common/http";

import { AppRoutingModule, routingComponents } from "../app.routing.module";
import { AppComponent } from "./app.component";
import { MaterialModule } from "../material/material.module";

import { NgxJsonViewerModule } from "ngx-json-viewer";

// Services
import { AppInitService } from "../services/app.init";
import { AuthorizationService } from "../services/authorization.service";
import { OidcService } from './oidc/oidc.service';
import { AngularAppLogger } from "../angular-app-logger";

// Components
import { PatientDetailComponent } from "../patient.detail/patient.detail.component";
import { DefaultComponent } from "../default/default.component";
import { SpinnerComponent } from "../spinner/spinner.component";
import { MessageTrayComponent } from "../message-tray/message-tray.component";
import { SidebarbuttonComponent } from "../sidebarbutton/sidebarbutton.component";
import { MedicationsComponent } from "../medications/medications.component";
import { DialogComponent } from "../dialog/dialog.component";
import { ConfirmationDialogComponent } from "../confirm.dialog/confirmation.dialog.component";
import { PatientImportComponent } from "../patient.import/patient.import.component";
import { LandingPageComponent } from "../landing.page/landing.page.component";
import { LandingDialogComponent } from "../landing.dialog/landing.dialog.component";
import { DateRangePickerComponent } from "../date-range-picker/date-range-picker.component";

import { ObservationLabsComponent } from "../observation.labs/observation.labs.component";
import { ObservationVitalSignsComponent } from "../observation.vital-signs/observation.vital-signs.component";
import { ErrorComponent } from "../error/error.component";
import { InfoComponent } from "../info/info.component";

// Interceptors
import { HttpErrorInterceptor } from "../interceptors/error/http-error.interceptor";
import { NMDPAuthInterceptor } from '../interceptors/nmdpauth.interceptor';
import { HttpMockRequestInterceptor } from "../interceptors/mock/mock.http.interceptor";

// Other services and modules
import { OidcConfigModule } from "./oidc/oidc-config.module";
import { environment } from "src/environments/environment.mock";

// All other service imports
import { CibmtrPatientService } from "../patient/cibmtr-patient.service";
import { PatientResolver } from "../patient/patient.resolver";
import { FhirService } from "../patient/fhir.service";
import { ObservationLabsService } from "../observation.labs/observation.labs.service";
import { ObservationVitalSignsService } from "../observation.vital-signs/observation.vital-signs.service";
import { SpinnerService } from "../spinner/spinner.service";
import { SidenavService } from "../sidenav.service";
import { MedicationsService } from "../medications/medications.service";
import { MedicationsResolver } from "../medications/medications.resolver";
import { Validator } from "../shared/constants/validator_regex";
import { MatchsyncSmartPiNgModule } from "@matchsync/smart-pi-ng";
import { ProcessingOverlay } from "../processing-overlay/processing-overlay";
import { provideMarkdown } from "ngx-markdown";

const isMock = environment.mock;

@NgModule({
  declarations: [
    AppComponent,
    routingComponents,
    PatientDetailComponent,
    DefaultComponent,
    ObservationLabsComponent,
    ObservationVitalSignsComponent,
    ErrorComponent,
    InfoComponent,
    SpinnerComponent,
    MessageTrayComponent,
    SidebarbuttonComponent,
    MedicationsComponent,
    DialogComponent,
    ConfirmationDialogComponent,
    PatientImportComponent,
    LandingPageComponent,
    LandingDialogComponent,
    DateRangePickerComponent,
  ],
    imports: [
        BrowserModule,
        AppRoutingModule,
        FormsModule,
        BrowserAnimationsModule,
        NgxJsonViewerModule,
        OidcConfigModule,
        MaterialModule,
        MatchsyncSmartPiNgModule,
        ProcessingOverlay
    ],
  providers: [
    // Core Services
    AppInitService,
    AuthorizationService,
    OidcService,

    // EHR Services
    PatientResolver,
    FhirService,
    CibmtrPatientService,
    ObservationLabsService,
    ObservationVitalSignsService,
    MedicationsService,
    MedicationsResolver,

    // UI Services
    SpinnerService,
    SidenavService,
    Validator,

    // App Initializer (EHR only)
    {
      provide: APP_INITIALIZER,
      useFactory: appInitializerFactory,
      deps: [AppInitService],
      multi: true
    },

    // Error Handling
    {
      provide: ErrorHandler,
      useClass: AngularAppLogger,
    },

    // HTTP Interceptors
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpErrorInterceptor,
      multi: true,
    },
    // AuthInterceptor for NMDP API calls
    {
      provide: HTTP_INTERCEPTORS,
      useClass: NMDPAuthInterceptor,
      multi: true,
    },

    // Mock Interceptor (conditional)
    ...(isMock ? [{
      provide: HTTP_INTERCEPTORS,
      useClass: HttpMockRequestInterceptor,
      multi: true,
    }] : []),

    // Angular Providers
    provideHttpClient(withXhr(), withInterceptorsFromDi()),
    provideMarkdown(),
    { provide: Window, useValue: window },
  ],
  bootstrap: [AppComponent]
})
export class AppModule {}

export function appInitializerFactory(appInitService: AppInitService) {
  return (): Promise<any> => {
      return appInitService.initializeApp();
  };
}
